import { useState, useEffect } from "react";

const SAVED_ACCOUNT_KEY = "quant_saved_account";
type Mode = "pwd" | "code";

export default function Login() {
  const [account, setAccount] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [mode, setMode] = useState<Mode>("pwd");
  const [rememberMe, setRememberMe] = useState(false);
  const [isReg, setIsReg] = useState(false);
  const [isForgot, setIsForgot] = useState(false);
  const [newPwd, setNewPwd] = useState("");
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [loading, setLoading] = useState(false);
  const [codeSent, setCodeSent] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [showPwd, setShowPwd] = useState(false);

  useEffect(() => {
    const saved = localStorage.getItem(SAVED_ACCOUNT_KEY);
    if (saved) { setAccount(saved); setRememberMe(true); }
  }, []);

  useEffect(() => {
    if (countdown <= 0) return;
    const t = setTimeout(() => setCountdown(c => c - 1), 1000);
    return () => clearTimeout(t);
  }, [countdown]);

  const sendCode = async () => {
    setError(""); setInfo("");
    if (!account.trim()) { setError("请输入手机号或邮箱"); return; }
    setLoading(true);
    try {
      const res = await fetch("/api/auth/send-code", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ identifier: account.trim() }),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.error || "发送失败"); return; }
      setCodeSent(true);
      setCountdown(60);
      setInfo(data.message || "验证码已发送");
    } catch { setError("网络错误，请重试");
    } finally { setLoading(false); }
  };

  const handleForgot = async () => {
    setError(""); setInfo("");
    if (!account.trim()) { setError("请输入手机号或邮箱"); return; }
    if (!code.trim()) { setError("请先获取验证码"); return; }
    if (!newPwd || newPwd.length < 6) { setError("新密码至少6位"); return; }
    setLoading(true);
    try {
      const res = await fetch("/api/auth/reset-password", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ identifier: account.trim(), code: code.trim(), newPassword: newPwd }),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.error || "重置失败"); return; }
      setInfo("密码已重置，请用新密码登录");
      setTimeout(() => { setIsForgot(false); setCode(""); setNewPwd(""); setInfo(""); }, 2000);
    } catch { setError("网络错误，请重试");
    } finally { setLoading(false); }
  };

  const handle = async () => {
    if (isForgot) { handleForgot(); return; }
    setError(""); setInfo("");
    setLoading(true);
    try {
      let url = "", body: Record<string, unknown> = {};
      if (isReg) {
        url = "/api/auth/register";
        body = { identifier: account.trim(), password, code: code.trim() };
      } else if (mode === "code") {
        url = "/api/auth/login-with-code";
        body = { identifier: account.trim(), code: code.trim() };
      } else {
        url = "/api/auth/login";
        body = { identifier: account.trim(), password };
      }
      const res = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.error || "操作失败"); return; }
      if (!isReg && rememberMe) localStorage.setItem(SAVED_ACCOUNT_KEY, account.trim());
      else if (!isReg && !rememberMe) localStorage.removeItem(SAVED_ACCOUNT_KEY);
      window.location.href = "/";
    } catch { setError("网络错误，请重试");
    } finally { setLoading(false); }
  };

  const onKey = (e: React.KeyboardEvent) => { if (e.key === "Enter") handle(); };
  const switchMode = (m: Mode) => { setMode(m); setError(""); setInfo(""); setCode(""); setCodeSent(false); };
  const switchReg = (reg: boolean) => { setIsReg(reg); setIsForgot(false); setError(""); setInfo(""); setCode(""); setCodeSent(false); setPassword(""); setNewPwd(""); };

  return (
    <>
      <style>{`
        .login-root *{box-sizing:border-box;margin:0;padding:0;}
        .login-root{min-height:100vh;min-height:100dvh;display:flex;align-items:center;justify-content:center;
          background:linear-gradient(135deg,#0f172a 0%,#1e2d4a 50%,#0f172a 100%);
          font-family:-apple-system,BlinkMacSystemFont,'PingFang SC','Microsoft YaHei','Helvetica Neue',sans-serif;
          padding:16px;position:relative;overflow:hidden;}
        .login-root::before{content:'';position:absolute;inset:0;
          background-image:linear-gradient(rgba(99,155,255,.05) 1px,transparent 1px),linear-gradient(90deg,rgba(99,155,255,.05) 1px,transparent 1px);
          background-size:40px 40px;pointer-events:none;}
        .lg-glow1{position:absolute;width:500px;height:500px;border-radius:50%;
          background:radial-gradient(circle,rgba(59,130,246,.13) 0%,transparent 70%);top:-150px;left:-150px;pointer-events:none;}
        .lg-glow2{position:absolute;width:400px;height:400px;border-radius:50%;
          background:radial-gradient(circle,rgba(139,92,246,.10) 0%,transparent 70%);bottom:-100px;right:-100px;pointer-events:none;}
        .lg-card{position:relative;width:100%;max-width:420px;
          background:rgba(30,45,74,.95);backdrop-filter:blur(20px);-webkit-backdrop-filter:blur(20px);
          border:1px solid rgba(255,255,255,.10);border-radius:20px;padding:28px 32px;
          box-shadow:0 0 0 1px rgba(59,130,246,.08),0 24px 64px rgba(0,0,0,.55),0 0 60px rgba(59,130,246,.06);}
        .lg-card::before{content:'';position:absolute;top:0;left:20%;right:20%;height:1px;
          background:linear-gradient(90deg,transparent,rgba(99,155,255,.6),transparent);pointer-events:none;}
        .lg-logo{display:flex;align-items:center;gap:10px;margin-bottom:18px;}
        .lg-logo-icon{width:38px;height:38px;border-radius:11px;
          background:linear-gradient(135deg,#2563eb,#7c3aed);
          display:flex;align-items:center;justify-content:center;font-size:20px;
          box-shadow:0 4px 14px rgba(37,99,235,.40);flex-shrink:0;}
        .lg-logo-name{color:#f1f5f9;font-size:16px;font-weight:700;line-height:1.2;letter-spacing:.2px;}
        .lg-logo-sub{color:#64748b;font-size:10.5px;text-transform:uppercase;letter-spacing:1.2px;margin-top:1px;}
        .lg-h{color:#f8fafc;font-size:22px;font-weight:700;letter-spacing:-.3px;margin-bottom:4px;}
        .lg-sub{color:#7a90a8;font-size:13px;margin-bottom:18px;}
        .lg-mode-tabs{display:flex;gap:0;margin-bottom:14px;border-bottom:1px solid rgba(255,255,255,.10);}
        .lg-mode-tab{flex:1;padding:9px 0;border:none;background:transparent;
          color:#64748b;font-size:14px;font-weight:500;cursor:pointer;font-family:inherit;
          border-bottom:2px solid transparent;transition:all .2s;margin-bottom:-1px;}
        .lg-mode-tab.on{color:#93c5fd;border-bottom-color:#3b82f6;}
        .lg-field{margin-bottom:12px;}
        .lg-label{display:block;color:#94a3b8;font-size:12px;font-weight:500;margin-bottom:7px;letter-spacing:.3px;}
        .lg-iw{position:relative;display:flex;gap:8px;}
        .lg-input{width:100%;background:rgba(255,255,255,.07);border:1px solid rgba(255,255,255,.12);
          border-radius:10px;padding:10px 14px;color:#f1f5f9;font-size:15px;font-family:inherit;
          outline:none;transition:border-color .2s,box-shadow .2s;-webkit-appearance:none;}
        .lg-input::placeholder{color:#4a5f7a;}
        .lg-input:focus{border-color:rgba(99,155,255,.55);box-shadow:0 0 0 3px rgba(59,130,246,.10);}
        .lg-input.pr{padding-right:46px;}
        .lg-input.code{letter-spacing:6px;font-size:20px;font-weight:600;text-align:center;color:#93c5fd;}
        .lg-eye{position:absolute;right:12px;top:50%;transform:translateY(-50%);
          background:none;border:none;cursor:pointer;color:#64748b;
          padding:4px;display:flex;align-items:center;transition:color .2s;}
        .lg-eye:hover{color:#94a3b8;}
        .lg-send-btn{flex-shrink:0;white-space:nowrap;background:rgba(37,99,235,.18);
          border:1px solid rgba(99,155,255,.35);border-radius:10px;
          padding:0 16px;color:#93c5fd;font-size:13px;font-weight:500;
          cursor:pointer;font-family:inherit;transition:all .2s;}
        .lg-send-btn:hover:not(:disabled){background:rgba(37,99,235,.30);}
        .lg-send-btn:disabled{opacity:.5;cursor:not-allowed;}
        .lg-hint{color:#4a6282;font-size:11px;margin-top:5px;}
        .lg-rem{display:flex;align-items:center;gap:8px;margin-bottom:14px;
          cursor:pointer;user-select:none;width:fit-content;}
        .lg-rem input[type=checkbox]{width:15px;height:15px;accent-color:#3b82f6;cursor:pointer;}
        .lg-rem span{color:#64748b;font-size:13px;}
        .lg-err{display:flex;align-items:center;gap:8px;
          background:rgba(239,68,68,.09);border:1px solid rgba(239,68,68,.22);
          border-radius:8px;padding:9px 12px;color:#fca5a5;font-size:13px;margin-bottom:14px;}
        .lg-info{display:flex;align-items:center;gap:8px;
          background:rgba(34,197,94,.09);border:1px solid rgba(34,197,94,.22);
          border-radius:8px;padding:9px 12px;color:#86efac;font-size:13px;margin-bottom:14px;}
        .lg-btn{width:100%;background:linear-gradient(135deg,#2563eb 0%,#7c3aed 100%);
          border:none;border-radius:10px;padding:13px 0;color:#fff;
          font-size:15px;font-weight:600;font-family:inherit;cursor:pointer;
          transition:opacity .2s,transform .1s,box-shadow .2s;
          box-shadow:0 4px 20px rgba(37,99,235,.32);letter-spacing:.3px;
          position:relative;overflow:hidden;}
        .lg-btn::after{content:'';position:absolute;inset:0;background:linear-gradient(rgba(255,255,255,.08),transparent);}
        .lg-btn:hover:not(:disabled){opacity:.9;box-shadow:0 6px 26px rgba(37,99,235,.42);}
        .lg-btn:active:not(:disabled){transform:scale(.99);}
        .lg-btn:disabled{opacity:.55;cursor:not-allowed;}
        .lg-note{text-align:center;color:#4a5f7a;font-size:12px;margin-top:14px;line-height:1.8;}
        .lg-link{color:#60a5fa;text-decoration:none;cursor:pointer;font-size:12px;}
        .lg-link:hover{text-decoration:underline;}
        @keyframes spin{to{transform:rotate(360deg);}}
        .lg-spin{display:inline-block;width:13px;height:13px;
          border:2px solid rgba(255,255,255,.25);border-top-color:#fff;
          border-radius:50%;animation:spin .7s linear infinite;
          vertical-align:middle;margin-right:6px;}
        @media(max-width:480px){.lg-card{padding:20px 16px;border-radius:16px;}.lg-h{font-size:20px;}}
        @media(max-width:360px){.lg-card{padding:22px 14px;}}
      `}</style>

      <div className="login-root">
        <div className="lg-glow1"/><div className="lg-glow2"/>
        <div className="lg-card">

          <div className="lg-logo">
            <div className="lg-logo-icon">📈</div>
            <div>
              <div className="lg-logo-name">AI选股与量化交易系统</div>
              <div className="lg-logo-sub">Quant Trading Platform</div>
            </div>
          </div>

          <h2 className="lg-h">{isReg ? "创建账号" : isForgot ? "重置密码" : "欢迎回来"}</h2>
          <p className="lg-sub">{isReg ? "注册后即可访问决策驾驶舱" : isForgot ? "验证身份后重置登录密码" : "登录以进入决策驾驶舱"}</p>

          {/* 登录方式 Tab（仅登录时显示） */}
          {!isReg && !isForgot && (
            <div className="lg-mode-tabs">
              <button className={`lg-mode-tab${mode === "pwd" ? " on" : ""}`} onClick={() => switchMode("pwd")}>密码登录</button>
              <button className={`lg-mode-tab${mode === "code" ? " on" : ""}`} onClick={() => switchMode("code")}>验证码登录</button>
            </div>
          )}

          {/* 账号 */}
          <div className="lg-field">
            <label className="lg-label">手机号 / 邮箱</label>
            <input className="lg-input" type="text" inputMode="email" autoComplete="username"
              placeholder="请输入手机号或邮箱" value={account}
              onChange={e => setAccount(e.target.value)} onKeyDown={onKey}/>
          </div>

          {/* 密码（密码模式 或 注册） */}
          {(isReg || (!isForgot && mode === "pwd")) && (
            <div className="lg-field">
              <label className="lg-label">{isReg ? "设置密码（至少 6 位）" : "密码"}</label>
              <div style={{position:"relative"}}>
                <input className="lg-input pr"
                  type={showPwd ? "text" : "password"}
                  autoComplete={isReg ? "new-password" : "current-password"}
                  placeholder={isReg ? "设置登录密码" : "请输入密码"}
                  value={password} onChange={e => setPassword(e.target.value)} onKeyDown={onKey}/>
                <button className="lg-eye" type="button" onClick={() => setShowPwd(v => !v)} tabIndex={-1}>
                  {showPwd ? (
                    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94"/>
                      <path d="M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19"/>
                      <line x1="1" y1="1" x2="23" y2="23"/>
                    </svg>
                  ) : (
                    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                      <circle cx="12" cy="12" r="3"/>
                    </svg>
                  )}
                </button>
              </div>
            </div>
          )}

          {/* 验证码（注册时 / 验证码登录模式 / 忘记密码） */}
          {(isReg || isForgot || (!isReg && mode === "code")) && (
            <div className="lg-field">
              <label className="lg-label">验证码</label>
              <div className="lg-iw">
                <input className="lg-input code" type="text" inputMode="numeric"
                  maxLength={6} placeholder="──────"
                  value={code} onChange={e => setCode(e.target.value.replace(/\D/g,""))} onKeyDown={onKey}/>
                <button className="lg-send-btn" onClick={sendCode}
                  disabled={loading || countdown > 0}>
                  {countdown > 0 ? `${countdown}s` : codeSent ? "重新获取" : "获取验证码"}
                </button>
              </div>
              <p className="lg-hint">{isReg ? "验证码将发送至您的邮箱/手机，10 分钟内有效" : isForgot ? "输入验证码后设置新密码" : "验证码将发送至注册邮箱，10 分钟内有效"}</p>
            </div>
          )}

          {/* 忘记密码：新密码输入 */}
          {isForgot && (
            <div className="lg-field">
              <label className="lg-label">新密码（至少 6 位）</label>
              <div style={{position:"relative"}}>
                <input className="lg-input pr" type={showPwd ? "text" : "password"}
                  placeholder="设置新密码" value={newPwd}
                  onChange={e => setNewPwd(e.target.value)} onKeyDown={onKey}/>
                <button className="lg-eye" type="button" onClick={() => setShowPwd(v => !v)} tabIndex={-1}>
                  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                </button>
              </div>
            </div>
          )}

          {/* 记住我 */}
          {!isReg && !isForgot && (
            <label className="lg-rem">
              <input type="checkbox" checked={rememberMe} onChange={e => setRememberMe(e.target.checked)}/>
              <span>记住我（保持登录状态）</span>
            </label>
          )}

          {info && <div className="lg-info"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>{info}</div>}
          {error && <div className="lg-err"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>{error}</div>}

          <button className="lg-btn" onClick={handle} disabled={loading || (mode === "code" && !isReg && code.length < 6)}>
            {loading && <span className="lg-spin"/>}
            {loading ? "处理中..." : isReg ? "注册账号" : isForgot ? "重置密码" : "登录"}
          </button>

          <div className="lg-note">
            {isReg ? (
              <>已有账号？<a className="lg-link" onClick={() => switchReg(false)}>直接登录</a></>
            ) : isForgot ? (
              <><a className="lg-link" onClick={() => { setIsForgot(false); setError(""); setInfo(""); setCode(""); setNewPwd(""); }}>← 返回登录</a></>
            ) : (
              <span>
                <a className="lg-link" onClick={() => { setIsForgot(true); setError(""); setInfo(""); setCode(""); setMode("pwd"); }}>忘记密码？</a>
                {/* [Claude 2026-05-22 Phase 2.5] 注册入口重启 */}
                <span style={{margin:"0 8px",color:"#3a4f6a"}}>·</span>
                <a className="lg-link" onClick={() => { window.location.href = "/register"; }}>有邀请码? 立即注册</a>
              </span>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
